import { useEffect, useRef } from "react";
import { Box, Dialog, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import CountdownClock from "../CountdownClock";
import { useCustomColors } from "../../hooks/useCustomColors";
import type { RevealBet, RoundOutcome, RoundReveal } from "../../api/reveal";
import { BET_OPTIONS } from "../../utils/gameRules";

/**
 * The reveal ceremony, on screen.
 *
 * Three acts in one modal: the round counts down, its numbers open one position
 * at a time, then the player's own slip is judged. Which act is showing is read
 * straight off `reveal.phase` and `revealed.length` — this component runs no
 * timers and decides nothing. `useRoundReveal` owns all of that, so the two
 * cannot disagree about where the ceremony is.
 */

export interface RoundRevealDialogProps {
  open: boolean;
  reveal: RoundReveal | null;
  outcome: RoundOutcome | null;
  /** The numbers opened so far. The rest render as empty slots. */
  revealed: number[];
  numbersComplete: boolean;
  secondsToDraw: number;
  currency: string;
  onClose: () => void;
}

const formatMoney = (currency: string, cents: number) =>
  `${cents < 0 ? "−" : ""}${currency} ${Math.abs(cents / 100).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;


/** Below this a slip fits on screen, so scrolling it would only be motion. */
const AUTOSCROLL_FROM_BETS = 6;

/** One row per beat, so the walk reads as a list rather than a jump. */
const ROW_STEP_MS = 900;

/** The `gap` on the rows, which the step has to clear as well as the row. */
const ROW_GAP_PX = 8;

/** "Big", "Odd", "7" — what the player actually backed, for the result rows. */
const betLabel = (bet: RevealBet, t: (key: string) => string) => {
  if (bet.betType === "number") return String(bet.pickedNumber ?? "—");
  const option = BET_OPTIONS.find((item) => item.option === bet.betType.toUpperCase());
  return option ? t(option.labelKey) : bet.betType;
};

const RoundRevealDialog = ({
  open,
  reveal,
  outcome,
  revealed,
  numbersComplete,
  secondsToDraw,
  currency,
  onClose,
}: RoundRevealDialogProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();

  /**
   * A long slip scrolls inside its own box.
   *
   * The rows are the only part that grows without bound, so they get the
   * height and the scrollbar and the dialog around them stays put. It then
   * walks itself down a row at a time so a player with sixteen bets sees each
   * one — skipped when the slip fits, when motion is not wanted, and the
   * moment the player scrolls it themselves.
   */
  const listRef = useRef<HTMLDivElement>(null);
  const betCount = outcome?.bets.length ?? 0;
  const showingResult = numbersComplete && !!outcome?.hasBets;

  useEffect(() => {
    const list = listRef.current;
    if (!showingResult || betCount < AUTOSCROLL_FROM_BETS || !list) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const row = list.firstElementChild as HTMLElement | null;
    const step = (row?.offsetHeight ?? 44) + ROW_GAP_PX;
    const atEnd = () => list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

    // The walk is a courtesy, not a ride: the first touch of the wheel or the
    // scrollbar hands the list back to the player for good.
    let timer = 0;
    const stop = () => {
      window.clearInterval(timer);
      list.removeEventListener("wheel", stop);
      list.removeEventListener("touchstart", stop);
    };

    timer = window.setInterval(() => {
      if (atEnd()) return stop();
      list.scrollBy?.({ top: step, behavior: "smooth" });
    }, ROW_STEP_MS);

    list.addEventListener("wheel", stop, { passive: true });
    list.addEventListener("touchstart", stop, { passive: true });

    return stop;
  }, [betCount, showingResult]);

  if (!reveal) return null;

  const counting = reveal.phase === "lockdown" || reveal.phase === "drawing";
  const hasNumbers = reveal.results.length > 0;
  /*
   * The result card waits for the last number.
   *
   * It is the payoff of the whole animation, and putting it beside a half-open
   * row of numbers gives the ending away before the reveal has finished.
   */
  const showOutcome = numbersComplete && !!outcome?.hasBets;
  const won = outcome?.outcome === "win";

  const headlineColour = won ? "#FFF8E8" : colors.text.primary;
  /* Win and loss are told apart before a word is read: gold runs through the
     winning panel, the losing one stays deep red and keeps the cup out. */
  const summaryFill = won
    ? `linear-gradient(135deg, #8E1216 0%, ${colors.accent.primary} 28%, ${colors.accent.gold} 52%, ${colors.accent.primary} 76%, #8E1216 100%)`
    : "linear-gradient(135deg, #4A0D0C 0%, #6E1210 55%, #380A0A 100%)";
  /* Win and loss share one row shape — only the accent, fill and glow differ,
     so a slip of six reads as one list however it went. */
  const outcomeRowSx = (isWinner: boolean) => ({
    px: "12px",
    py: "10px",
    borderRadius: "10px",
    background: isWinner
      ? `linear-gradient(100deg, ${colors.tint.goldStrong} 0%, ${colors.tint.gold} 58%, transparent 100%)`
      : `linear-gradient(100deg, ${colors.tint.primary} 0%, ${colors.tint.soft} 58%, transparent 100%)`,
    border: `1px solid ${isWinner ? colors.accent.gold : colors.border.subtle}`,
    boxShadow: isWinner ? colors.glow.gold : "none",
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "420px",
          maxWidth: "100%",
          m: 2,
          borderRadius: "20px",
          overflow: "hidden",
          textAlign: "center",
          backgroundColor: colors.card.dialog,
          backgroundImage: "none",
          border: `1px solid ${won ? colors.accent.gold : colors.border.accent}`,
          boxShadow: colors.card.dialogShadow,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: colors.tint.scrim } }}
    >
      <Box sx={{ px: "28px", py: "22px", background: colors.gradient.dialogHeader }}>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "3px",
            color: colors.accent.gold,
          }}
        >
          {t(`reveal_phase_${reveal.phase}`)}
        </Typography>
        <Typography
          key={reveal.drawNo}
          // The class arrives with the numbers, so the title slams in as the
          // result lands — and only then, since the node is never remounted.
          className={reveal.results.length ? "fx-kaboom" : undefined}
          sx={{
            mt: "6px",
            fontFamily: "Montserrat, sans-serif",
            fontSize: "20px",
            fontWeight: 800,
            fontStyle: "italic",
            color: "#FFFFFF",
            // The effect owns no colour of its own; it reads these.
            "--ink": "#FFFFFF",
            "--ink-2": colors.accent.gold,
            "--ink-3": colors.accent.danger,
          }}
        >
          {reveal.periodCode}
        </Typography>
      </Box>

      <Box sx={{ px: "28px", py: "26px", background: colors.card.dialogBody }}>
        {/* ── Act one: the countdown ─────────────────────────────────── */}
        {counting && (
          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "3px",
                color: colors.text.secondary,
              }}
            >
              {t("reveal_drawing_in")}
            </Typography>
            <CountdownClock
              seconds={secondsToDraw}
              sx={{
                mt: "10px",
                fontFamily: "Montserrat, sans-serif",
                fontSize: "56px",
                fontWeight: 800,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
                color: colors.accent.gold,
                textShadow: colors.glow.gold,
                // A round past its close with no numbers yet: the digits have
                // stopped meaning anything, so they breathe rather than sit at
                // a dead zero.
                animation: secondsToDraw === 0 ? "revealPulse 1.4s ease-in-out infinite" : "none",
                "@keyframes revealPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.45 },
                },
              }}
            />
          </Box>
        )}

        {/* ── Act two: the numbers ───────────────────────────────────── */}
        {hasNumbers && (
          <Box sx={{ mt: counting ? "24px" : 0 }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "3px",
                color: colors.text.secondary,
              }}
            >
              {t("reveal_winning_numbers")}
            </Typography>
            <Stack
              direction="row"
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mt: "14px", gap: "10px" }}
            >
              {reveal.results.map((value, index) => {
                const landed = index < revealed.length;
                return (
                  <Box
                    key={`${reveal.drawNo}-${index}`}
                    className={`lotto-ball ${landed ? "reveal-number-live" : "reveal-number-hidden"}`}
                    sx={{
                      width: "44px",
                      height: "44px",
                      display: "grid",
                      placeItems: "center",
                      perspective: "600px",
                      borderRadius: "50%",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "19px",
                      fontWeight: 800,
                      color: landed ? colors.text.primary : "transparent",
                      backgroundColor: landed
                        ? colors.tint.goldStrong
                        : colors.background.panelSecondary,
                      border: `1px solid ${landed ? colors.border.focus : colors.border.subtle}`,
                      borderStyle: landed ? "solid" : "dashed",
                      boxShadow: landed ? colors.glow.gold : "none",
                    }}
                  >
                    {landed ? <Box component="span" className="reveal-number-flip">{value}</Box> : ""}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* ── Act three: this player's result ────────────────────────── */}
        {showOutcome && outcome && (
          <Box sx={{ mt: "24px", pt: "20px", borderTop: `1px solid ${colors.border.subtle}` }}>
            <Box
              sx={{
                position: "relative",
                isolation: "isolate",
                overflow: "hidden",
                p: "16px",
                borderRadius: "14px",
                textAlign: "center",
                background: summaryFill,
                border: `1px solid ${won ? colors.accent.gold : colors.accent.danger}`,
                boxShadow: won ? colors.glow.gold : "none",
              }}
            >
              {won && (
                <Box
                  component="img"
                  src="/assets/win-coin.webp"
                  alt=""
                  aria-hidden="true"
                  className="fx-cup"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: "132px",
                    height: "132px",
                    m: "-66px 0 0 -66px",
                    objectFit: "contain",
                    // Behind the text inside this panel's own stacking context.
                    zIndex: -1,
                    pointerEvents: "none",
                  }}
                />
              )}
              <Typography
                className={won ? "fx-sweep" : undefined}
                sx={{
                  display: "inline-block",
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: won ? { xs: "26px", md: "28px" } : "22px",
                  fontWeight: 800,
                  fontStyle: "italic",
                  letterSpacing: won ? "0.4px" : undefined,
                  color: headlineColour,
                  textShadow: "0 2px 6px rgba(20, 4, 4, 0.75)",
                }}
              >
                {won
                  ? t("reveal_you_won", { amount: formatMoney(currency, outcome.summary.payoutCents) })
                  : t("reveal_you_lost")}
              </Typography>
              <Typography
                sx={{
                  mt: "6px",
                  fontSize: "12px",
                  color: won ? "#FFF0D0" : colors.text.secondary,
                  textShadow: "0 1px 4px rgba(20, 4, 4, 0.7)",
                }}
              >
                {t("reveal_won_of", {
                  won: outcome.summary.wonCount,
                  total: outcome.summary.betCount,
                })}
              </Typography>
            </Box>

            <Stack
              ref={listRef}
              sx={{
                mt: "16px",
                gap: "8px",
                maxHeight: { xs: "38vh", md: "240px" },
                overflowY: "auto",
                overscrollBehavior: "contain",
                pr: "4px",
              }}
            >
              {outcome.bets.map((bet) => (
                <Stack
                  key={bet.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={outcomeRowSx(!!bet.isWinner)}
                >
                  <Typography sx={{ fontSize: "12.5px", color: colors.text.secondary }}>
                    {t("game_bet_position_value", { position: bet.position })} ·{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: colors.text.primary }}>
                      {betLabel(bet, t)}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      position: "relative",
                      isolation: "isolate",
                      pr: bet.isWinner ? "26px" : 0,
                      fontSize: "13px",
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      color: bet.isWinner ? colors.accent.gold : colors.accent.danger,
                      textShadow: bet.isWinner ? colors.glow.gold : "none",
                      // Winners only: the cup as it is, at its own colours,
                      // sat beside the payout rather than washed out under it.
                      ...(bet.isWinner && {
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: "50%",
                          width: "26px",
                          height: "26px",
                          mt: "-13px",
                          zIndex: -1,
                          backgroundImage: "url(/assets/win-cup.webp)",
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          pointerEvents: "none",
                        },
                      }),
                    }}
                  >
                    {bet.isWinner
                      ? `+${formatMoney(currency, bet.potentialPayoutCents)}`
                      : `−${formatMoney(currency, bet.stakeCents)}`}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Typography sx={{ mt: "14px", fontSize: "11.5px", color: colors.text.disabled }}>
              {t("reveal_totals", {
                stake: formatMoney(currency, outcome.summary.stakeCents),
                payout: formatMoney(currency, outcome.summary.payoutCents),
                net: `${outcome.summary.netCents >= 0 ? "+" : ""}${formatMoney(
                  currency,
                  outcome.summary.netCents
                )}`,
              })}
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default RoundRevealDialog;
